import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ShieldCheck, Loader2, AlertCircle, Trash2, EyeOff, History, Download,
  Mail, RefreshCw, CheckCircle2, XCircle,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AdminStatusStrip from '../components/AdminStatusStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-only Data Governance page.
 *
 * Surfaces enforcement of the HussarAi doctrine:
 *     "Raw data expires. Process intelligence compounds."
 *
 * Lets the admin run a dry-run cleanup (no writes) or a real cleanup that
 * anonymises / hard-deletes expired operational records. Process intelligence
 * events are never touched.
 */
const DataGovernance = () => {
  const [runs, setRuns] = useState([]);
  const [scheduler, setScheduler] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Resend integration health (admin-only diagnostic, no live API call).
  const [resendHealth, setResendHealth] = useState(null);
  const [resendError, setResendError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const loadResendHealth = useCallback(async () => {
    setResendLoading(true); setResendError('');
    try {
      const r = await axios.get(`${API}/admin/resend/health`,
        { withCredentials: true });
      setResendHealth(r.data || null);
    } catch (e) {
      setResendError(
        e?.response?.data?.detail || 'Nie udało się pobrać statusu Resend.'
      );
      setResendHealth(null);
    } finally {
      setResendLoading(false);
    }
  }, []);

  // Email-drain (E2/E3 follow-up) audit trail.
  const [drainRuns, setDrainRuns] = useState([]);
  const [drainTotal, setDrainTotal] = useState(0);
  const [drainLoading, setDrainLoading] = useState(false);
  const [drainBusy, setDrainBusy] = useState(false);
  const [drainError, setDrainError] = useState('');

  const loadDrainRuns = useCallback(async () => {
    setDrainLoading(true); setDrainError('');
    try {
      const r = await axios.get(`${API}/admin/email-drain/runs?limit=25`,
        { withCredentials: true });
      setDrainRuns(r.data?.rows || []);
      setDrainTotal(r.data?.total || 0);
    } catch (e) {
      setDrainError(
        e?.response?.data?.detail || 'Nie udało się pobrać historii drain workera.'
      );
    } finally {
      setDrainLoading(false);
    }
  }, []);

  const triggerDrainRun = useCallback(async () => {
    setDrainBusy(true); setDrainError('');
    try {
      await axios.post(`${API}/admin/email-drain/run`, {},
        { withCredentials: true });
      await loadDrainRuns();
    } catch (e) {
      setDrainError(
        e?.response?.data?.detail || 'Ręczne uruchomienie drain workera nie powiodło się.'
      );
    } finally {
      setDrainBusy(false);
    }
  }, [loadDrainRuns]);

  const loadRuns = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [runsR, statusR] = await Promise.all([
        axios.get(`${API}/admin/data-governance/runs?limit=10`,
                  { withCredentials: true }),
        axios.get(`${API}/admin/data-governance/scheduler-status`,
                  { withCredentials: true }),
      ]);
      setRuns(runsR.data.items || []);
      setScheduler(statusR.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się pobrać historii uruchomień.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRuns(); loadResendHealth(); loadDrainRuns(); },
    [loadRuns, loadResendHealth, loadDrainRuns]);

  const runCleanup = async (dryRun) => {
    setBusy(true); setError(''); setLastResult(null);
    try {
      const r = await axios.post(`${API}/admin/data-governance/run-retention-cleanup`,
        { dryRun }, { withCredentials: true });
      setLastResult(r.data);
      await loadRuns();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się uruchomić procesu retencji.');
    } finally {
      setBusy(false);
    }
  };

  const exportAuditCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/data-governance/runs.csv`, {
        params: { days: 90 },
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hussarai-retention-runs-90d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się wyeksportować audytu CSV.');
    }
  };

  const last = runs[0];

  return (
    <div className="hussar-landing" data-testid="data-governance-page">
      <header className="hussar-landing-header">
        <div className="hussar-landing-header-content">
          <Link to="/admin/process-patterns" className="hussar-tc-back" data-testid="dg-back">
            <ArrowLeft className="w-4 h-4" /> Raport wzorców
          </Link>
          <div className="hussar-landing-header-actions">
            <span className="hussar-tc-meta">Admin · Data Governance</span>
          </div>
        </div>
      </header>

      <main className="hussar-agenci-main">
        <AdminStatusStrip />
        <section className="hussar-agenci-hero" data-testid="dg-hero">
          <div className="hussar-agenci-eyebrow">
            <ShieldCheck className="w-3 h-3 inline-block mr-1" /> DATA GOVERNANCE · RETENCJA
          </div>
          <h1 className="hussar-agenci-h1" data-testid="dg-h1">Retencja i anonimizacja</h1>
          <p className="hussar-agenci-subhead" data-testid="dg-subhead">
            HussarAi egzekwuje retencję danych operacyjnych. Wiedza procesowa pozostaje zagregowana i anonimowa.
          </p>
          <p className="hussar-tc-meta" data-testid="dg-doctrine">
            „Raw data expires. Process intelligence compounds.”
          </p>
        </section>

        <section className="hussar-pol-op-section" data-testid="dg-controls">
          <Card className="hussar-tc-card">
            <h3 className="hussar-agenci-h2" style={{ fontSize: '18px' }}>Uruchom retencję</h3>
            <p className="hussar-tc-meta" style={{ marginTop: '8px' }}>
              Tryb próbny (dry-run) tylko liczy rekordy, nic nie zmienia. Uruchomienie pełne
              anonimizuje rekordy z polityką &ldquo;anonymize&rdquo; i usuwa rekordy z polityką &ldquo;hard_delete&rdquo;.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
              <Button size="sm" variant="outline" onClick={() => runCleanup(true)} disabled={busy}
                      data-testid="dg-dry-run">
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                Dry-run
              </Button>
              <Button size="sm" onClick={() => runCleanup(false)} disabled={busy}
                      className="hussar-btn-primary"
                      data-testid="dg-run-cleanup">
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Uruchom retencję
              </Button>
              <Button size="sm" variant="outline" onClick={loadRuns} disabled={busy}
                      data-testid="dg-refresh">
                <History className="w-3 h-3 mr-1" /> Odśwież historię
              </Button>
              <Button size="sm" variant="outline" onClick={exportAuditCsv}
                      data-testid="dg-export-audit-csv">
                <Download className="w-3 h-3 mr-1" /> Eksport audytu CSV
              </Button>
            </div>
            <p className="hussar-tc-meta" style={{ marginTop: '8px' }}
               data-testid="dg-export-audit-helper">
              Eksport zawiera wyłącznie metadane uruchomień retencji. Nie zawiera danych osobowych, treści spraw ani dokumentów.
            </p>
            {error && (
              <p className="hussar-compliance-error" style={{ marginTop: '10px' }}
                 data-testid="dg-error">{error}</p>
            )}
          </Card>
        </section>

        {lastResult && (
          <section className="hussar-pol-op-section" data-testid="dg-last-result">
            <Card className="hussar-tc-card">
              <div className="hussar-compliance-output-badge">
                <AlertCircle className="w-4 h-4" />
                {lastResult.dryRun ? 'Dry-run wynik' : 'Wynik uruchomienia retencji'}
              </div>
              <SummaryGrid data={lastResult} />
            </Card>
          </section>
        )}

        <section className="hussar-pol-op-section" data-testid="dg-last-run-card">
          <Card className="hussar-tc-card">
            <h3 className="hussar-agenci-h2" style={{ fontSize: '18px' }}>Ostatnie uruchomienie</h3>
            {loading && <p className="hussar-tc-meta" style={{ marginTop: '10px' }}>
              <Loader2 className="w-3 h-3 inline-block animate-spin mr-1" /> Ładuję…
            </p>}
            {!loading && !last && <p className="hussar-tc-meta" style={{ marginTop: '10px' }}>
              Brak zapisów uruchomień w historii.
            </p>}
            {last && <SummaryGrid data={last} />}
          </Card>
        </section>

        <section className="hussar-pol-op-section" data-testid="dg-scheduler-card">
          <Card className="hussar-tc-card">
            <h3 className="hussar-agenci-h2" style={{ fontSize: '18px' }}>Harmonogram</h3>
            {scheduler ? (
              <>
                <p className="hussar-tc-meta" style={{ marginTop: '10px' }} data-testid="dg-scheduler-status">
                  Scheduler: {scheduler.enabled ? <strong>włączony</strong> : <strong>wyłączony</strong>}
                  {' · '}
                  Stan: {scheduler.running ? 'pracuje' : 'oczekuje'}
                  {' · '}
                  Godzina: <strong>{String(scheduler.hourUtc).padStart(2, '0')}:00 UTC</strong>
                </p>
                {scheduler.lastScheduledRun ? (
                  <p className="hussar-tc-meta" data-testid="dg-scheduler-last">
                    Ostatnie uruchomienie automatyczne: <code>{scheduler.lastScheduledRun.createdAt}</code>
                    {' · '}matched=<strong>{scheduler.lastScheduledRun.matchedExpired}</strong>{' '}
                    anonymized=<strong>{scheduler.lastScheduledRun.anonymized}</strong>{' '}
                    hardDeleted=<strong>{scheduler.lastScheduledRun.hardDeleted}</strong>
                  </p>
                ) : (
                  <p className="hussar-tc-meta" data-testid="dg-scheduler-last-empty">
                    Brak uruchomień automatycznych w historii.
                  </p>
                )}
              </>
            ) : (
              <p className="hussar-tc-meta" style={{ marginTop: '10px' }}>
                Status harmonogramu niedostępny.
              </p>
            )}
          </Card>
        </section>

        {/* Resend integration health — admin diagnostic. No live API call
            is performed by either side: the endpoint reads from Mongo, and
            this card only re-fetches when the user clicks Odśwież. */}
        <section className="hussar-pol-op-section" data-testid="dg-resend-health-card">
          <Card className="hussar-tc-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
              <h3 className="hussar-agenci-h2" style={{ fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Mail className="w-4 h-4" /> Status integracji Resend
              </h3>
              <Button
                size="sm"
                variant="outline"
                onClick={loadResendHealth}
                disabled={resendLoading}
                data-testid="dg-resend-refresh"
              >
                {resendLoading
                  ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  : <RefreshCw className="w-3 h-3 mr-1" />}
                Odśwież
              </Button>
            </div>

            {resendError && (
              <p className="hussar-compliance-error" style={{ marginTop: '10px' }}
                 data-testid="dg-resend-error">{resendError}</p>
            )}

            {!resendError && resendHealth && (
              <div
                className="hussar-resend-health-grid"
                data-testid="dg-resend-grid"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginTop: '14px' }}
              >
                <div>
                  <p className="hussar-tc-meta">Klucz API</p>
                  <p data-testid="dg-resend-key" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {resendHealth.api_key_present
                      ? <CheckCircle2 className="w-4 h-4" style={{ color: '#00D4FF' }} />
                      : <XCircle className="w-4 h-4" style={{ color: '#ff6b6b' }} />}
                    <code>{resendHealth.api_key_masked || '—'}</code>
                  </p>
                </div>
                <div>
                  <p className="hussar-tc-meta">Nadawca</p>
                  <p data-testid="dg-resend-sender"><code>{resendHealth.sender || '—'}</code></p>
                </div>
                <div>
                  <p className="hussar-tc-meta">Powiadomienia wewnętrzne</p>
                  <p data-testid="dg-resend-internal"><code>{resendHealth.internal_notify || '—'}</code></p>
                </div>
                <div>
                  <p className="hussar-tc-meta">Ostatnia wysyłka</p>
                  <p data-testid="dg-resend-last-status">
                    <strong>{resendHealth.last_send_status || '—'}</strong>
                    {resendHealth.last_send_at && (
                      <>
                        {' · '}
                        <code style={{ fontSize: '11px' }}>{resendHealth.last_send_at}</code>
                      </>
                    )}
                  </p>
                  {resendHealth.last_send_collection && (
                    <p className="hussar-tc-meta" data-testid="dg-resend-last-collection">
                      źródło: <code>{resendHealth.last_send_collection}</code>
                    </p>
                  )}
                </div>
                {resendHealth.last_resend_error && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <p className="hussar-tc-meta">Ostatni błąd Resend</p>
                    <p data-testid="dg-resend-last-error"
                       style={{ color: '#ff6b6b', fontSize: '13px', wordBreak: 'break-word' }}>
                      {resendHealth.last_resend_error}
                    </p>
                  </div>
                )}
                <div style={{ gridColumn: '1 / -1' }}>
                  <p className="hussar-tc-meta" data-testid="dg-resend-checked-at">
                    Sprawdzono: <code>{resendHealth.checked_at}</code>
                  </p>
                </div>
              </div>
            )}

            <p className="hussar-tc-meta" style={{ marginTop: '14px' }} data-testid="dg-resend-helper">
              Endpoint <code>GET /api/admin/resend/health</code> nie wykonuje
              żywego wywołania do Resend — czyta status ostatniej wysyłki z bazy.
              Pełny klucz API nie jest nigdy zwracany; widoczny jest tylko prefiks 8 znaków.
            </p>
          </Card>
        </section>

        {/* E2/E3 follow-up drain — audit trail + manual trigger. Side-effect-
            free read by default; the "Uruchom teraz" button is the only
            action that actually sends emails. */}
        <section className="hussar-pol-op-section" data-testid="dg-email-drain-card">
          <Card className="hussar-tc-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
              <h3 className="hussar-agenci-h2" style={{ fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Mail className="w-4 h-4" /> Drain follow-upów (E2/E3)
              </h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={loadDrainRuns}
                  disabled={drainLoading || drainBusy}
                  data-testid="dg-drain-refresh"
                >
                  {drainLoading
                    ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    : <RefreshCw className="w-3 h-3 mr-1" />}
                  Odśwież
                </Button>
                <Button
                  size="sm"
                  onClick={triggerDrainRun}
                  disabled={drainBusy}
                  data-testid="dg-drain-run-now"
                  className="hussar-btn-primary"
                >
                  {drainBusy
                    ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    : null}
                  Uruchom teraz
                </Button>
              </div>
            </div>

            {drainError && (
              <p className="hussar-compliance-error" style={{ marginTop: '10px' }}
                 data-testid="dg-drain-error">{drainError}</p>
            )}

            <p className="hussar-tc-meta" style={{ marginTop: '10px' }} data-testid="dg-drain-total">
              Łącznie uruchomień: <strong>{drainTotal}</strong>{' '}
              (pokazuję ostatnie {drainRuns.length})
            </p>

            {drainRuns.length === 0 && !drainLoading && (
              <p className="hussar-tc-meta" style={{ marginTop: '10px' }}>(brak)</p>
            )}

            {drainRuns.length > 0 && (
              <div data-testid="dg-drain-table" style={{ overflowX: 'auto', marginTop: '10px' }}>
                <table className="hussar-runs-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--hussar-text-secondary)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <th style={{ padding: '8px 6px' }}>Uruchomienie</th>
                      <th style={{ padding: '8px 6px' }}>Trigger</th>
                      <th style={{ padding: '8px 6px', textAlign: 'right' }}>Sprawdzone</th>
                      <th style={{ padding: '8px 6px', textAlign: 'right' }}>Należne</th>
                      <th style={{ padding: '8px 6px', textAlign: 'right' }}>Wysłane</th>
                      <th style={{ padding: '8px 6px', textAlign: 'right' }}>Pending</th>
                      <th style={{ padding: '8px 6px', textAlign: 'right' }}>Błędy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drainRuns.map((r) => (
                      <tr
                        key={r.runId || r.createdAt}
                        data-testid={`dg-drain-row-${r.runId}`}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                      >
                        <td style={{ padding: '8px 6px' }}>
                          <code style={{ fontSize: '11px' }}>{r.createdAt}</code>
                        </td>
                        <td style={{ padding: '8px 6px' }}>{r.trigger || '—'}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{r.checkedSubmissions ?? 0}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{r.dueCount ?? 0}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: '#00D4AA' }}>{r.sentCount ?? 0}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: '#F5C542' }}>{r.pendingCount ?? 0}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: (r.errorCount > 0) ? '#FF5C5C' : undefined }}>{r.errorCount ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="hussar-tc-meta" style={{ marginTop: '14px' }} data-testid="dg-drain-helper">
              Worker uruchamia się automatycznie co 15 minut (planowo), drenując
              kolejki <code>scheduledEmails[*].status=&quot;scheduled&quot;</code> których
              <code>sendAt</code> już minął. &bdquo;Uruchom teraz&rdquo; wymusza pojedynczy przebieg.
            </p>
          </Card>
        </section>

        <section className="hussar-pol-op-section" data-testid="dg-history">
          <Card className="hussar-tc-card">
            <h3 className="hussar-agenci-h2" style={{ fontSize: '18px' }}>Historia uruchomień</h3>
            {runs.length === 0 && (
              <p className="hussar-tc-meta" style={{ marginTop: '10px' }}>(brak)</p>
            )}
            {runs.length > 0 && (
              <ul className="hussar-pol-op-bullets" style={{ marginTop: '10px' }}>
                {runs.map((r) => (
                  <li key={r.id || r.createdAt}>
                    <code>{r.createdAt}</code> · {r.trigger || (r.dryRun ? 'dry-run' : 'live')} ·
                    {' '}matched=<strong>{r.matchedExpired}</strong>{' '}
                    anonymized=<strong>{r.anonymized}</strong>{' '}
                    hardDeleted=<strong>{r.hardDeleted}</strong>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>

        <footer className="hussar-pol-op-footer" data-testid="dg-footer">
          <p>HussarAi · Data Governance · Admin only</p>
          <p className="hussar-pol-op-footer-meta">
            Dane operacyjne wygasają. Wiedza procesowa rośnie.
          </p>
        </footer>
      </main>
    </div>
  );
};

const SummaryGrid = ({ data }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '10px', marginTop: '12px',
  }}>
    <Stat label="Tryb"          value={data.dryRun ? 'dry-run' : 'live'} />
    <Stat label="Zbiory"        value={(data.checkedCollections || []).length} />
    <Stat label="Wygasłych"     value={data.matchedExpired} />
    <Stat label="Anonimizowanych" value={data.anonymized} />
    <Stat label="Hard-deleted"  value={data.hardDeleted} />
    <Stat label="Błędy"         value={(data.errors || []).length} />
  </div>
);

const Stat = ({ label, value }) => (
  <div style={{
    padding: '10px', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '8px', background: 'rgba(255,255,255,0.02)',
  }}>
    <div style={{ fontSize: '10px', letterSpacing: '0.12em',
                  textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>
      {label}
    </div>
    <div style={{ fontSize: '20px', fontWeight: 600, marginTop: '4px' }}>{String(value)}</div>
  </div>
);

export default DataGovernance;
